import React from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "../../UIElements/Icon";
import {
  getUserName,
  getUserPhone,
  getUserInitials,
  getUserOrdersCount,
} from "../../../../constants/userConstants";

const UserCard = ({ user, onViewDetails }) => {
  const { t } = useTranslation("customers");
  const rawName = getUserName(user);
  const name = rawName === "User" ? t("card.userFallback") : rawName;
  const phone = getUserPhone(user, t("card.noData"));
  const email = user?.email || t("card.noEmail");
  const initials = getUserInitials(user);
  const ordersCount = getUserOrdersCount(user);
  const levelLabelByUserLevel = {
    0: t("levels.admin"),
    2: t("levels.businessOwner"),
    3: t("levels.customer"),
  };
  const levelLabel = levelLabelByUserLevel[user?.level] || t("levels.user");
  const CardElement = onViewDetails ? "button" : "div";

  const handleViewDetails = () => {
    if (onViewDetails) {
      onViewDetails(user);
    }
  };

  return (
    <CardElement
      {...(onViewDetails
        ? {
            type: "button",
            onClick: handleViewDetails,
            "aria-label": name,
          }
        : {})}
      data-user-state={user.enabled ? "active" : "inactive"}
      className={`border-border bg-card text-card-foreground focus-visible:ring-ring hover:border-primary/60 hover:bg-accent/40 w-full rounded-lg border p-3 text-start shadow-sm transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none sm:p-4 ${
        onViewDetails ? "cursor-pointer" : ""
      } ${user.enabled ? "" : "opacity-70"}`}
    >
      {/* Header with Avatar */}
      <div className="mb-3 flex items-center space-x-2 sm:space-x-3">
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full sm:h-10 sm:w-10">
          {user.photo ? (
            <img
              src={user.photo}
              alt={name}
              className="h-full w-full object-cover"
              onError={(e) => {
                // Fallback to initials if image fails to load
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
          ) : null}
          <div
            className={`h-full w-full items-center justify-center text-xs font-semibold sm:text-sm ${
              user.enabled
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground"
            } ${user.photo ? "hidden" : "flex"}`}
          >
            {initials}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-foreground truncate text-sm font-semibold sm:text-base">
            {name}
          </h3>
          <p className="text-muted-foreground text-xs">{levelLabel}</p>
        </div>
      </div>

      {/* Contact Information */}
      <div className="mb-3 space-y-1">
        <div className="text-muted-foreground flex items-center gap-2">
          <Icon name="Phone" className="h-3 w-3 flex-shrink-0" />
          <span className="truncate text-xs">{phone}</span>
        </div>
        <div className="text-muted-foreground flex items-center gap-2">
          <Icon name="Mail" className="h-3 w-3 flex-shrink-0" />
          <span className="truncate text-xs">{email}</span>
        </div>
      </div>

      {/* Statistics */}
      {user?.level !== 0 && (
        <div className="grid grid-cols-1 gap-2 sm:gap-3">
          <div className="text-center">
            <p className="text-muted-foreground text-xs">{t("card.orders")}</p>
            <p className="text-foreground text-xs font-semibold sm:text-sm">
              {ordersCount || "0"}
            </p>
          </div>
        </div>
      )}
    </CardElement>
  );
};

export default UserCard;
